import { getBucketData } from 'lib/utilityFunctions';

import gameIds from '../data/GameIds';
import GameCategories from '../data/GameCategories';
import { GRADES, CURIOSO_POWER, SKILL_STATE, SKILL_CATEGORY } from '../data/constants';
import { persistStateTypes } from '../../reducers/persistStateReducer';

const curiosoPowerDefault = [
  {
    active: false,
    type: CURIOSO_POWER.BOOK_MAGIC,
  },
  {
    active: false,
    type: CURIOSO_POWER.INSPIRATION,
  },
  {
    active: false,
    type: CURIOSO_POWER.MIND_READER,
  },
];

const CURIOSO_POWER_PROGRESS_QUEST = {
  [CURIOSO_POWER.BOOK_MAGIC]: 6,
  [CURIOSO_POWER.INSPIRATION]: 12,
  [CURIOSO_POWER.MIND_READER]: 18,
};

const updateSkillsProgression = (state, json, product) => {
  const profileSkills = json;
  const gameContentSets = [...state.GameContentSets];
  const skillsObject = {
    Sounds: {
      state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
      totalSkills: 0,
      skillsCompleted: 0,
      skillsList: [],
      lastGamesPlayed: [],
    },
    Letters: {
      state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
      totalSkills: 0,
      skillsCompleted: 0,
      skillsList: [],
      lastGamesPlayed: [],
    },
    Images: {
      state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
      totalSkills: 0,
      skillsCompleted: 0,
      skillsList: [],
      lastGamesPlayed: [],
    },
    Ideas: {
      state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
      totalSkills: 0,
      skillsCompleted: 0,
      skillsList: [],
      lastGamesPlayed: [],
    },
    Words: {
      state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
      totalSkills: 0,
      skillsCompleted: 0,
      skillsList: [],
      lastGamesPlayed: [],
    },
  };

  gameContentSets.forEach((set) => {
    // skip ereader for now(ereader has no student skill assidned yet)
    if (set.Content[0].StudentSkill !== '' && set.Content[0].StudentSkill !== 'Books') {
      if (state.placementByAdaptivity === set.Content[0].Placement) {
        skillsObject[(set.Content[0].StudentSkill).replace(/ /g, '')].skillsList.push(set.Skill);
      }
      const skillElement = profileSkills.Data.SkillLevels.find(elem =>
        elem.Skill === set.Skill && elem.Source === 'level_result');
      if (skillElement) {
        skillsObject[(set.Content[0].StudentSkill).replace(/ /g, '')].skillsCompleted
          += Math.min(skillElement.Level, 1);
        skillsObject[(set.Content[0].StudentSkill).replace(/ /g, '')].lastGamesPlayed.push({
          ...skillElement,
          product,
          gameId: gameContentSets.find(content => content.Skill === skillElement.Skill).Content[0].GameId,
        });
      }
    }
  });

  [
    SKILL_CATEGORY.SOUNDS,
    SKILL_CATEGORY.LETTERS,
    SKILL_CATEGORY.IMAGES,
    SKILL_CATEGORY.IDEAS,
    SKILL_CATEGORY.WORDS,
  ].forEach((skill) => {
    skillsObject[skill].totalSkills = skillsObject[skill].skillsList.length;
    // resort games data
    skillsObject[skill].lastGamesPlayed = skillsObject[skill].lastGamesPlayed.sort((a, b) => {
      if (a.CreateDate > b.CreateDate) {
        return 1;
      }
      if (a.CreateDate < b.CreateDate) {
        return -1;
      }
      return 0;
    });
    if (skillsObject[skill].totalSkills === 0) {
      skillsObject[skill].state = SKILL_STATE.INACTIVE;
    } else if (skillsObject[skill].skillsCompleted >= skillsObject[skill].totalSkills) {
      skillsObject[skill].state = SKILL_STATE.COMPLETED;
    } else {
      skillsObject[skill].state = SKILL_STATE.OPEN;
    }
  });

  return {
    ...state,
    skillsDescription: {
      ...state.skillsDescription,
      Sounds: {
        ...state.skillsDescription.Sounds,
        totalSkills: skillsObject.Sounds.totalSkills,
        skillsCompleted: skillsObject.Sounds.skillsCompleted,
        state: skillsObject.Sounds.state,
        lastGamesPlayed: skillsObject.Sounds.lastGamesPlayed,
      },
      Letters: {
        ...state.skillsDescription.Letters,
        totalSkills: skillsObject.Letters.totalSkills,
        skillsCompleted: skillsObject.Letters.skillsCompleted,
        state: skillsObject.Letters.state,
        lastGamesPlayed: skillsObject.Letters.lastGamesPlayed,
      },
      Images: {
        ...state.skillsDescription.Images,
        totalSkills: skillsObject.Images.totalSkills,
        skillsCompleted: skillsObject.Images.skillsCompleted,
        state: skillsObject.Images.state,
        lastGamesPlayed: skillsObject.Images.lastGamesPlayed,
      },
      Ideas: {
        ...state.skillsDescription.Ideas,
        totalSkills: skillsObject.Ideas.totalSkills,
        skillsCompleted: skillsObject.Ideas.skillsCompleted,
        state: skillsObject.Ideas.state,
        lastGamesPlayed: skillsObject.Ideas.lastGamesPlayed,
      },
      Words: {
        ...state.skillsDescription.Words,
        totalSkills: skillsObject.Words.totalSkills,
        skillsCompleted: skillsObject.Words.skillsCompleted,
        state: skillsObject.Words.state,
        lastGamesPlayed: skillsObject.Words.lastGamesPlayed,
      },
    },
  };
};

const getAdaptivityPlacement = (placementPeriod, placementGrade) => {
  if (!placementPeriod || !placementGrade) {
    return '';
  }

  const grade = [GRADES.KINDERGARTEN, GRADES.PRE_KINDERGARTEN].includes(placementGrade) ? 'K' : Number(placementGrade);

  if (!grade || grade > 3) {
    return '';
  }

  return `${placementPeriod}${grade}`;
};

const getCuriosoPowersByQuestProgress = (currentQuestId, powers) => {
  const questId = Number(currentQuestId);

  if (Number.isNaN(questId)) {
    return powers;
  }

  return curiosoPowerDefault.map(({ type }) => ({
    type,
    active: questId > CURIOSO_POWER_PROGRESS_QUEST[type] || questId === 0,
  }));
};

export const initialState = {
  version: 1,
  grade: 'K',
  placementByAdaptivity: '',
  gameSkills: {},
  skillsByGameId: {},
  curiosoPower: curiosoPowerDefault,
  skillsDescription:
    {
      Sounds: {
        categories: [
          GameCategories.PHONOLOGICAL_AWARENESS,
          GameCategories.LETTER_SOUND_CORRESPONDENCE,
          GameCategories.LETTER_COMBINATIONS,
        ],
        state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
        totalSkills: 0,
        skillsCompleted: 0,
        lastGamesPlayed: [],
      },
      Letters: {
        categories: [
          GameCategories.EARLY_DECODING,
          GameCategories.ADVANCED_DECODING,
        ],
        state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
        totalSkills: 0,
        skillsCompleted: 0,
        lastGamesPlayed: [],
      },
      Images: {
        categories: [
          GameCategories.MICROCOMPREHENSION,
        ],
        state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
        totalSkills: 0,
        skillsCompleted: 0,
        lastGamesPlayed: [],
      },
      Ideas: {
        categories: [
          GameCategories.KEY_IDEAS_AND_DETAILS,
          GameCategories.INTEGRATION_OF_KNOWLEDGE_AND_IDEAS,
          GameCategories.CRAFT_AND_STRUCTURE,
        ],
        state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
        totalSkills: 0,
        skillsCompleted: 0,
        lastGamesPlayed: [],
      },
      Words: {
        categories: [
          GameCategories.VOCABULARY,
        ],
        state: SKILL_STATE.INACTIVE, // state of skill button on Power Tab
        totalSkills: 0,
        skillsCompleted: 0,
        lastGamesPlayed: [],
      },
    },
  GameContentSets: [],
};
let power = [];
let index;

const getGameSkills = (skillSet) => {
  const gameSkillMap = {};
  skillSet.forEach((skill) => {
    gameSkillMap[skill.Content[0].GameId] = skill.SkillName;
  });
  return gameSkillMap;
};

const getSkillsByGameId = (skillSet) => skillSet?.reduce((acc, skill) => {
  skill.Content.forEach((game) => {
    game.Levels.forEach((level) => {
      acc[game.GameId] = {
        ...acc[game.GameId],
        [level]: { skillId: skill.Skill, skillName: skill.SkillName },
      };
    });
  });

  return acc;
}, {});

const powerReducer = (state = initialState, action) => {
  if (action.gameId !== gameIds.METAGAME) {
    return state;
  }
  switch (action.type) {
    case 'ENABLE_POWER':
      power = Object.assign([], state.curiosoPower);
      index = power.findIndex(powerItem =>
        powerItem.type === action.payload.power,
      );
      power[index].active = true;
      return {
        ...state,
        curiosoPower: power,
      };
    case persistStateTypes.REQUESTRESPONSE_GAMESTATE_ALL: {
      const { placementData, stateData } = action;
      const { placementPeriod, placementGrade } = placementData || {};
      const { currentActiveQuestId } = getBucketData(stateData[gameIds.METAGAME])?.gameDetails || {};
      const { curiosoPower: savedCuriosoPowers } =
        getBucketData(stateData[gameIds.METAGAME_CURIOSO_POWERS])?.gameDetails || {};

      const curiosoPower = !savedCuriosoPowers?.length
        ? getCuriosoPowersByQuestProgress(currentActiveQuestId, state.curiosoPower)
        : savedCuriosoPowers;
      const placementByAdaptivity = getAdaptivityPlacement(placementPeriod, placementGrade);

      return {
        ...state,
        curiosoPower,
        placementByAdaptivity,
      };
    }

    case persistStateTypes.LOAD_USER_SKILLS:
      return {
        ...state,
        GameContentSets: action.skillsSet,
        gameSkills: getGameSkills(action.skillsSet),
        skillsByGameId: getSkillsByGameId(action.skillsSet),
      };

    case persistStateTypes.SET_PROFILE:
      return updateSkillsProgression(state, action.json, action.product);

    default:
      return state;
  }
};

export default powerReducer;
